
import { CameraInventory } from "@/types/inventory";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AdapterSection } from "./AdapterSection";

interface CameraInfoCardProps {
  camera: CameraInventory;
  hasAdapter: boolean;
  setHasAdapter: (value: boolean) => void;
  adapterMounts: string[];
  handleAddAdapterMount: (mountName: string) => Promise<void>;
  handleRemoveAdapterMount: (mountName: string) => Promise<void>;
  toggleAdapterCompatibility: () => Promise<void>;
}

export function CameraInfoCard({
  camera,
  hasAdapter,
  setHasAdapter,
  adapterMounts,
  handleAddAdapterMount,
  handleRemoveAdapterMount,
  toggleAdapterCompatibility
}: CameraInfoCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'working':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'needs_repair':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
      case 'for_parts':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatCameraType = (type: string) => {
    if (type === 'dslr') return 'DSLR';
    if (type === 'slr') return 'SLR';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatFormat = (format: string | null) => {
    if (!format) return "N/A";
    if (format === 'aps-c') return 'APS-C';
    if (format === 'full_frame') return 'Full Frame';
    if (format === 'medium_format') return 'Medium Format';
    if (format === 'large_format') return 'Large Format';
    return format.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatMount = (mount: string | null) => {
    if (!mount) return "N/A";
    if (mount === 'canon_ef') return 'Canon EF';
    if (mount === 'nikon_f') return 'Nikon F';
    if (mount === 'sony_e') return 'Sony E';
    if (mount === 'leica_m') return 'Leica M';
    if (mount === 'pentax_k') return 'Pentax K';
    if (mount === 'minolta_md') return 'Minolta MD';
    if (mount === 'olympus_om') return 'Olympus OM';
    if (mount === 'm42') return 'M42';
    if (mount === 'hasselblad') return 'Hasselblad';
    return mount.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const canHaveInterchangeableLenses = () => {
    return camera.camera_type !== 'point_and_shoot';
  };

  const displayNotes = () => {
    if (!camera.notes) return '';
    return camera.notes;
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center mb-2">
          <CardTitle className="text-2xl">{camera.brand} {camera.model}</CardTitle>
          <Badge className={getStatusColor(camera.status || '')}>
            {formatStatus(camera.status || '')}
          </Badge>
        </div>
        <CardDescription>{formatCameraType(camera.camera_type || '')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {canHaveInterchangeableLenses() && (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1 font-medium">Mount</div>
              <div className="col-span-2">{formatMount(camera.mount)}</div>
            </div>
          )}
          
          <div className="grid grid-cols-3 gap-2">                  
            <div className="col-span-1 font-medium">Format</div>
            <div className="col-span-2">{formatFormat(camera.format)}</div>
            
            {camera.serial_number && (
              <>
                <div className="col-span-1 font-medium">Serial No.</div>
                <div className="col-span-2">{camera.serial_number}</div>
              </>
            )}
            
            {camera.purchase_date && (
              <>
                <div className="col-span-1 font-medium">Purchased</div>
                <div className="col-span-2">{camera.purchase_date}</div>
              </>
            )}
            
            {camera.purchase_price && (
              <>
                <div className="col-span-1 font-medium">Price</div>
                <div className="col-span-2">${camera.purchase_price}</div>
              </>
            )}
          </div>
          
          {canHaveInterchangeableLenses() && (
            <AdapterSection
              hasAdapter={hasAdapter}
              toggleAdapterCompatibility={toggleAdapterCompatibility}
              adapterMounts={adapterMounts}
              handleAddAdapterMount={handleAddAdapterMount}
              handleRemoveAdapterMount={handleRemoveAdapterMount}
              formatMount={formatMount}
            />
          )}
          
          {displayNotes() && (
            <div className="pt-4 border-t mt-4">
              <div className="font-medium mb-2">Notes</div>
              <div className="text-sm text-muted-foreground">
                {displayNotes()}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
